import { Helmet } from "react-helmet-async";
import { Plus } from "lucide-react";
import { Planner } from "@/components/Planner";
import { PlannerInstallNudge } from "@/components/PlannerInstallNudge";
import { Seo } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ITINERARY_3D, ITINERARY_45, spotById, spotDur } from "@/data/planner";

const SITE = "https://cphhighlights.com";

// A concrete, static example of what the planner actually produces — rendered
// as plain day-by-day HTML (real stops, durations and notes) so crawlers and AI
// agents can read what the tool does, not just the marketing copy. The planner
// itself is interactive/client-side; this is its output in readable form.
const SAMPLE_DAYS = [
  { title: "Day 1 — The waterfront & royal quarter", ids: ITINERARY_3D[0] },
  { title: "Day 2 — The old town & Tivoli", ids: ITINERARY_3D[1] },
  { title: "Day 3 — Christianshavn & Christiania", ids: ITINERARY_3D[2] },
  { title: "Day 4 — Vesterbro, from the Glyptotek to Kødbyen", ids: ITINERARY_45[0] },
  { title: "Day 5 — The coast: Kronborg & Louisiana", ids: ITINERARY_45[1] },
].map((d) => ({
  title: d.title,
  stops: d.ids.map((id) => spotById(id)).filter(Boolean).map((s) => ({
    name: s.name,
    dur: spotDur(s),
    note: s.note || "",
  })),
}));

const HOWTO_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to build a Copenhagen itinerary with the free planner",
  description:
    "Build a realistic day-by-day Copenhagen itinerary in a browser: set your dates, optionally add your hotel, pick the spots you want, and the planner groups them by neighbourhood, estimates times and splits them into sensible days you can save, share or print.",
  totalTime: "PT3M",
  step: [
    { "@type": "HowToStep", name: "Set your trip length or dates", text: "Choose how many days you have, or enter your exact arrival and departure dates and the half-days you're free — the plan is shaped to those calendar days and each spot's opening hours and season." },
    { "@type": "HowToStep", name: "Add your hotel (optional)", text: "Paste your hotel's Google Maps link so every day is routed to and from your door instead of criss-crossing the city." },
    { "@type": "HowToStep", name: "Pick the spots you want", text: "Choose from a local-curated list of sights, museums, bakeries, harbour baths and day trips — or paste any Google Maps link to add your own place." },
    { "@type": "HowToStep", name: "Get your day-by-day plan", text: "The planner groups nearby spots into single days, adds realistic durations and travel legs, a ~1h lunch and optional dinner, a per-day timeline clock and walking totals, warns about closing times, and flags any official Danish (and, for the Malmö day trip, Swedish) bank holiday on that day." },
    { "@type": "HowToStep", name: "Save, share or print", text: "Your plan auto-saves on your device. Save it to a short link or QR code, share it to Instagram, print it, or add it to your home screen as an app." },
  ],
};

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Trip Planner", item: `${SITE}/planner` },
  ],
};

const FEATURES = [
  "NEW — exact-date planning: enter your real arrival and departure dates and the plan is built for those calendar days, respecting each spot's weekday and seasonal opening hours",
  "NEW — arrival & departure half-days: tell it when you're free to explore on arrival day and when you must leave on the last day, and the plan trims each short day intelligently (a late arrival keeps Nyhavn and the canal tour, drops the far-flung stops first)",
  "NEW — hotel start point: paste your hotel's Google Maps link and days are routed to and from it, and a short last day swaps a far day trip for a shorter plan near your hotel",
  "NEW — smart lunch & dinner: a ~1h lunch break drops into each day at the right time, with an optional one-tap dinner suggestion — both meal rows can be moved earlier or later, or dismissed",
  "NEW — lock a day: pin a day you've perfected so it stays put while the rest of the trip recalculates around it",
  "NEW — Book Ahead panel: one list of every stop that needs a timed ticket, with direct booking links",
  "NEW — category toggles: show or hide bakeries, museums, or everything outside the city centre in one tap",
  "Automatic itineraries for any trip length — 1 day, 2 days, 3 days or up to 12 days",
  "Pick sights, museums, bakeries, harbour baths and day trips from a local-curated list",
  "Ready-made local itinerary built in one tap — 3, 4 or 5 days, including a Vesterbro day and a Louisiana + Helsingør coast day",
  "Automatic realistic time estimates for every stop, so days never get overbooked",
  "Groups spots that are close together into smooth single days — same-neighbourhood stops are never split across days",
  "Relaxed or Packed pace toggle — easier days with more of them, or fuller days with fewer",
  "Every day starts around 10:00 (room for breakfast) and ends somewhere that's open late",
  "Museum fatigue cap: never more than 2–3 museums in one day",
  "Per-day timeline clock (e.g. 10:00 → ~17:25) with real durations and travel legs",
  "Walking totals per day — minutes on foot and kilometres covered",
  "Closing-time awareness: warns if you'd arrive at a stop too close to closing time",
  "Skip hints on the ready-made days — 'short on time or need a lighter day? then skip this one'",
  "Opening-hours and season awareness — Christmas markets only appear in November–December, winter closing times respected",
  "Month-matched weather, daylight, what-to-pack and seasonal event tips",
  "Day trips (Louisiana, Helsingør & Kronborg, Malmö, Roskilde and more) slot in with train times included",
  "Paste any Google Maps link to add your own place as a stop",
  "Drag and drop stops to reorder them or move them between days — or move whole days",
  "Plans auto-save on your device as you build",
  "Save the plan and share it with a short link or QR code",
  "Print-friendly itinerary",
  "Works as an app — add it to your home screen and take the plan with you in the city",
  "Ready-made itinerary also readable at https://cphhighlights.com/copenhagen-itinerary",
];

const APP_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Copenhagen Planner — Build a Free Copenhagen Itinerary",
  url: `${SITE}/planner`,
  applicationCategory: "TravelApplication",
  operatingSystem: "Web",
  description:
    "Best Copenhagen itinerary. Build a free day-by-day Copenhagen itinerary in a couple of taps — for 1 day, 2 days, 3 days or longer. This advanced planner works from your exact arrival and departure dates and half-days: enter when you land and when you leave and each day is shaped to fit, respecting every spot's opening hours and season. Add your hotel by Google Maps link to route days from your door, get a smart ~1h lunch break (skipped when a sit-down spot already covers it) and an optional dinner, lock a day you've perfected, and see a Book Ahead list of every timed ticket. Pick the spots you fancy — we group what's close together, estimate how long every stop takes and split them into realistic days automatically, with a per-day timeline, walking totals, pace control and month-matched weather and packing tips. Save, share via link or QR code, print, or add it to your home screen. A ready-made 3-, 4- or 5-day itinerary is one tap away.",
  featureList: FEATURES,
  isAccessibleForFree: true,
  offers: { "@type": "Offer", price: "0", priceCurrency: "DKK" },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

// The planner's features as Q&A — visible on-page (native <details> keeps every
// answer in the rendered HTML) and mirrored in FAQPage JSON-LD below, so Google
// and AI agents read the same answers.
const FAQS = [
  {
    q: "How do I add my travel dates and times?",
    a: "Enter your arrival and departure dates — and, if you know them, the time you'll be free to explore on the day you land and the time you need to head off on your last day. Each day is shaped to fit those exact dates and hours; closing days and out-of-season spots are quietly handled for you, and any official Danish (or, for the Malmö day trip, Swedish) bank holiday is flagged on that day's card.",
  },
  {
    q: "Does the planner know about Danish and Swedish bank holidays?",
    a: "Yes. When you enter exact dates, any day that lands on an official Danish public holiday is flagged on that day's card so you know many shops close and attractions run reduced or holiday hours. The planner covers different bank holidays like Maundy Thursday (Skærtorsdag), Good Friday (Langfredag), Ascension Day (Kristi himmelfartsdag), Constitution Day (Grundlovsdag, 5 June) etc. If your trip includes the Malmö day trip across the Øresund, days are also checked against Swedish public holidays.",
  },
  {
    q: "Can I start the days from my hotel?",
    a: "Yes — paste your hotel's Google Maps link and every day is routed to and from your door, so you're never criss-crossing the city.",
  },
  {
    q: "Does the planner sort out meals?",
    a: "Yes — a roughly one-hour lunch break and an optional dinner drop into each day at the right time, and you can move either one earlier or later, or dismiss it.",
  },
  {
    q: "Can I pre-book the tickets I'll need?",
    a: "Yes — a Book Ahead panel lists every stop on your plan that needs a timed ticket, with direct booking links, so the busy spots are sorted before you go. You can also lock a day you've perfected so it stays put while the rest recalculates.",
  },
  {
    q: "Is the Copenhagen itinerary planner free?",
    a: "Yes — completely free, no account and no app download. Pick your spots and the plan builds itself in the browser. It also works as a home-screen app once you're in the city.",
  },
  {
    q: "How does the planner build my days?",
    a: "It groups spots that are close together so one day is one neighbourhood, never splitting an area across days. Every stop carries a realistic duration, days start around 10:00 and always end somewhere open late, museums are capped at 2–3 per day, and if you'd arrive somewhere too close to closing time the plan warns you.",
  },
  {
    q: "Can I choose between a relaxed and a packed trip?",
    a: "Yes — a pace toggle switches between Relaxed (easier days, more of them) and Packed (fuller days, fewer of them), and an on-screen plan re-packs instantly when you switch.",
  },
  {
    q: "Is there a ready-made Copenhagen itinerary?",
    a: "Yes — a local-written suggested itinerary for first time visitors for 1, 2, 3, 4 or 5 days loads with one tap, covering the royal quarter, the old town and Tivoli, Christianshavn, a Vesterbro day down Istedgade, and a Louisiana + Kronborg coast day. There's also a holiday-special Christmas itinerary that loads Nyhavn, D'Angleterre's decorated facade, the different Christmas markets, a gløgg stop and Tivoli's Christmas gardens.",
  },
  {
    q: "Can I add my own places to the plan?",
    a: "Yes — paste any Google Maps link and the place becomes a stop, clustered with whatever is nearest. You can also drag stops to reorder them or move them between days, and the plan auto-saves on your device.",
  },
  {
    q: "How do I take my Copenhagen plan with me?",
    a: "The website uses a cookie 🍪 to save your plan automatically, so it's there next time you open the page. Save it and share a short link or QR code with your travel partners, or print it. Add Cphhighlights.com to your phone's home screen like an app so it's always nearby.",
  },
  {
    q: "Can I share the plan on Instagram?",
    a: "Yes! When you're done, hit 'Story' and your phone opens to share the plan straight to your Instagram story. Hit 'Trip map' and you get a Copenhagen map with pins on your selected spots, ready to share to your story too.",
  },
  {
    q: "What does each day of the plan show?",
    a: "Each day shows a timeline clock (like 10:00 → ~17:25) plus walking totals in minutes and kilometres, any closing-time or out-of-season warnings, and a note if the date is an official Danish or Swedish bank holiday.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function PlannerPage() {
  return (
    <main data-testid="planner-page">
      <Seo
        title="Free Copenhagen Itinerary Planner — Advanced, Date-Aware"
        description="Build a free Copenhagen itinerary in a couple of taps. This advanced planner works from your exact travel dates and arrival/departure half-days, routes from your hotel, adds smart lunch and dinner, lets you lock a day, and lists every timed ticket to book ahead — grouping nearby spots and keeping every day realistic."
        path="/planner"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(APP_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(HOWTO_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <Planner />
      <section data-testid="planner-sample" className="max-w-3xl mx-auto px-5 pt-4 pb-10">
        <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl">
          What the planner builds — a sample itinerary
        </h2>
        <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">
          Here's an example of a finished plan for a first-time visitor — the kind of day-by-day
          route the planner produces in a couple of taps. Each day is one neighbourhood, grouped so
          you're never criss-crossing the city, with a realistic time at every stop. Open the planner
          above to build your own, change the length, add your hotel and dates, or swap any stop.
        </p>
        <ol className="mt-7 space-y-8">
          {SAMPLE_DAYS.map((day, i) => (
            <li key={i} data-testid={`planner-sample-day-${i + 1}`}>
              <h3 className="font-display font-900 tracking-tight text-lg md:text-xl">
                {day.title}
              </h3>
              <ul className="mt-3 space-y-3 border-l-2 border-black/10 pl-4">
                {day.stops.map((s, j) => (
                  <li key={j} className="font-body">
                    <p className="text-sm md:text-base font-800 text-black">
                      {s.name} <span className="font-700 text-black/45">· {s.dur}</span>
                    </p>
                    {s.note && <p className="text-sm text-black/65 leading-relaxed mt-0.5">{s.note}</p>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        <p className="font-body text-sm text-black/55 mt-7 leading-relaxed">
          A longer, written version of this route — with photos and tips — is at{" "}
          <a href="/copenhagen-itinerary" className="font-800 text-[#DF3226] underline underline-offset-2">
            the 3-day Copenhagen itinerary
          </a>
          .
        </p>
      </section>
      <section data-testid="planner-faq" className="max-w-3xl mx-auto px-5 pb-16">
        <h2 className="font-display font-900 text-2xl mb-5">Planner questions, answered</h2>
        <div className="divide-y divide-black/10 border-y border-black/10">
          {FAQS.map((f, i) => (
            <details key={i} data-testid={`planner-faq-${i}`} className="group py-4">
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-body font-800 text-base">
                {f.q}
                <Plus size={16} className="shrink-0 transition-transform group-open:rotate-45" />
              </summary>
              <p className="font-body text-sm text-black/70 leading-relaxed mt-2.5 max-w-2xl">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
      <ShareBar />
      <PlannerInstallNudge />
    </main>
  );
}
